import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from './firebase';
import { ArrowLeft, Mail } from 'lucide-react';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError(t('auth.resetNoAccount', 'No account found with this email address.'));
      } else {
        setError(err.message || t('auth.resetFailed', 'Failed to send reset email.'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="/logo.png" alt="BuildMyShifts" className="w-12 h-12 object-contain" />
            <h1 className="text-3xl font-bold text-gray-900">
              Build My <span className="text-primary">Shifts</span>
            </h1>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-green-600" size={32} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('auth.checkYourEmail', 'Check Your Email')}</h2>
              <p className="text-gray-600 mb-6">
                {t('auth.resetEmailSent', 'We’ve sent a password reset link to {{email}}. Please check your inbox and follow the instructions.', { email })}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                <ArrowLeft size={16} /> {t('auth.backToLogin', 'Back to Login')}
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('auth.forgotPassword')}</h2>
              <p className="text-gray-600 text-sm mb-6">
                {t('auth.resetIntro', 'Enter your email address and we’ll send you a link to reset your password.')}
              </p>

              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    placeholder={t('auth.emailPlaceholder', 'you@company.com')}
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {loading ? t('common.loading') : t('auth.sendResetLink', 'Send Reset Link')}
                </button>
              </form>

              <p className="mt-4 text-center text-sm text-gray-600">
                <Link to="/login" className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
                  <ArrowLeft size={14} /> {t('auth.backToLogin', 'Back to Login')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
